import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import type { Role, RolesFilters, PopulatedUser, RoleColumnKey } from '../types';
import { ROLES_READ_PERMISSIONS } from '../types';

interface RolesTableProps {
  roles: Role[];
  sortKey?: RolesFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort?: (key: RolesFilters['sortKey']) => void;
  onRowClick?: (role: Role) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  visibleColumns?: RoleColumnKey[];
  canModify?: boolean;
}

/**
 * Check if created_by is populated
 */
function isPopulatedUser(
  createdBy: string | PopulatedUser | undefined
): createdBy is PopulatedUser {
  return typeof createdBy === 'object' && createdBy !== null && '_id' in createdBy;
}

/**
 * Get creator display name
 */
function getCreatorName(createdBy: string | PopulatedUser | undefined): string {
  if (!createdBy) return '\u2014';
  if (typeof createdBy === 'string') return createdBy;
  if (isPopulatedUser(createdBy)) {
    const name = [createdBy.firstname, createdBy.lastname].filter(Boolean).join(' ');
    return name || createdBy.email || createdBy._id;
  }
  return '\u2014';
}

/**
 * Format date for display
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format permissions for display
 */
function formatPermissions(permissions: Role['permissions']): React.ReactNode {
  if (!permissions || permissions.length === 0) {
    return <span className="text-muted-foreground">No permissions</span>;
  }

  const allowCount = permissions.filter((p) => p.v === 'allow').length;
  const denyCount = permissions.filter((p) => p.v === 'deny').length;

  return (
    <div className="flex items-center gap-2">
      {allowCount > 0 && (
        <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
          {allowCount} allow
        </Badge>
      )}
      {denyCount > 0 && (
        <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
          {denyCount} deny
        </Badge>
      )}
    </div>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction: 1 | -1 }) {
  if (!active) {
    return <ChevronUp className="w-4 h-4 text-gray-300" />;
  }
  return direction === 1 ? (
    <ChevronUp className="w-4 h-4 text-gray-700" />
  ) : (
    <ChevronDown className="w-4 h-4 text-gray-700" />
  );
}

export function RolesTable({
  roles,
  sortKey,
  sortDirection = 1,
  onSort,
  onRowClick,
  page = 0,
  pageSize = 25,
  total = 0,
  onPageChange,
  visibleColumns,
  canModify = true,
}: RolesTableProps) {
  const { t } = useTranslation('roles');
  const { user } = useSession();
  const totalPages = Math.ceil(total / pageSize) || 1;

  // RBAC: check if user has any roles.read.* permission
  const permissions = user?.permission_strings ?? [];
  const hasAnyRolesReadPermission = permissions.some((p) => p.startsWith('roles.read.'));

  // Per-column RBAC checks (backwards compatible: if no specific permissions, show all)
  const canSeePermissions = !hasAnyRolesReadPermission || permissions.includes(ROLES_READ_PERMISSIONS.PERMISSIONS);
  const canSeeCreatedBy = !hasAnyRolesReadPermission || permissions.includes(ROLES_READ_PERMISSIONS.CREATED_BY);
  const canSeeCreatedAt = !hasAnyRolesReadPermission || permissions.includes(ROLES_READ_PERMISSIONS.CREATED_AT);

  const isColumnVisible = (col: RoleColumnKey): boolean => {
    // If visibleColumns is provided, check user toggle
    if (visibleColumns && !visibleColumns.includes(col)) return false;

    // Check RBAC
    switch (col) {
      case 'permissions': return canSeePermissions;
      case 'createdBy': return canSeeCreatedBy;
      case 'createdAt': return canSeeCreatedAt;
      default: return true; // roleId always visible
    }
  };

  const handleSort = (key: RolesFilters['sortKey']) => {
    if (onSort) {
      onSort(key);
    }
  };

  const SortableHeader = ({
    sortKeyVal,
    children,
    className,
  }: {
    sortKeyVal: RolesFilters['sortKey'];
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-gray-100 ${className || ''}`}
      onClick={() => handleSort(sortKeyVal)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon active={sortKey === sortKeyVal} direction={sortDirection} />
      </div>
    </TableHead>
  );

  if (roles.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        {t('table.noResults')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <SortableHeader sortKeyVal="roleId">{t('table.roleId')}</SortableHeader>
              {isColumnVisible('permissions') && (
                <TableHead>{t('table.permissions')}</TableHead>
              )}
              {isColumnVisible('createdBy') && (
                <TableHead>{t('table.createdBy')}</TableHead>
              )}
              {isColumnVisible('createdAt') && (
                <SortableHeader sortKeyVal="created_at">{t('table.createdAt')}</SortableHeader>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow
                key={role._id}
                className={canModify ? 'cursor-pointer hover:bg-muted/50' : 'hover:bg-muted/50'}
                onClick={() => canModify && onRowClick?.(role)}
              >
                <TableCell>
                  <span className="font-medium">{role.roleId}</span>
                </TableCell>
                {isColumnVisible('permissions') && (
                  <TableCell>{formatPermissions(role.permissions)}</TableCell>
                )}
                {isColumnVisible('createdBy') && (
                  <TableCell className="text-muted-foreground">
                    {getCreatorName(role.created_by)}
                  </TableCell>
                )}
                {isColumnVisible('createdAt') && (
                  <TableCell className="text-muted-foreground">
                    {formatDate(role.created_at)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
